import { airbnbStayKind } from '@/calendar/bookingListDisplay';
import {
  bookingBoundaryDateKey,
  dateKeyFromDate,
  getBookingsForDay,
  type PwaCalendarBooking,
} from '@/calendar/pwaCalendarUtils';

/** Ventana [start, end). Ambas fechas son medianoche local. */
export type StayWindow = { start: Date; end: Date };

const MAX_WINDOW_DAYS = 92;

export function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function addLocalDays(date: Date, days: number): Date {
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
  return startOfLocalDay(next);
}

export function rangeForPreset(preset: 'today' | 'week' | 'fortnight', now = new Date()): StayWindow {
  const today = startOfLocalDay(now);
  if (preset === 'today') return { start: today, end: addLocalDays(today, 1) };
  if (preset === 'week') {
    const mondayOffset = (today.getDay() + 6) % 7;
    const monday = addLocalDays(today, -mondayOffset);
    return { start: monday, end: addLocalDays(monday, 7) };
  }
  return { start: today, end: addLocalDays(today, 14) };
}

export function rangeFromDateKeys(fromKey: string, toKey: string): StayWindow | null {
  const start = parseDateKey(fromKey);
  const endInclusive = parseDateKey(toKey);
  if (!start || !endInclusive || endInclusive < start) return null;
  const end = addLocalDays(endInclusive, 1);
  const days = Math.round((end.getTime() - start.getTime()) / 86_400_000);
  if (days < 1 || days > MAX_WINDOW_DAYS) return null;
  return { start, end };
}

export function parseDateKey(raw: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const [year, month, day] = raw.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return date;
}

export function eachLocalDay(range: StayWindow): Date[] {
  const days: Date[] = [];
  for (let cursor = startOfLocalDay(range.start); cursor < range.end; cursor = addLocalDays(cursor, 1)) {
    days.push(cursor);
    if (days.length > MAX_WINDOW_DAYS) break;
  }
  return days;
}

export function monthsOverlapping(range: StayWindow): Array<{ year: number; month: number }> {
  const last = addLocalDays(range.end, -1);
  const months: Array<{ year: number; month: number }> = [];
  let year = range.start.getFullYear();
  let month = range.start.getMonth() + 1;
  while (year < last.getFullYear() || (year === last.getFullYear() && month <= last.getMonth() + 1)) {
    months.push({ year, month });
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
    if (months.length > 6) break;
  }
  return months;
}

export function formatStayWindowLabel(range: StayWindow): string {
  const last = addLocalDays(range.end, -1);
  if (dateKeyFromDate(range.start) === dateKeyFromDate(last)) {
    return range.start.toLocaleDateString('es', { weekday: 'long', day: 'numeric', month: 'long' });
  }
  const startLabel = range.start.toLocaleDateString('es', { day: 'numeric', month: 'short' });
  const endLabel = last.toLocaleDateString('es', { day: 'numeric', month: 'short' });
  return `${startLabel} – ${endLabel}`;
}

export type StayWindowStats = {
  days: number;
  occupiedCabins: number;
  freeCabins: number;
  cabinCount: number;
  arrivals: number;
  departures: number;
  occupiedNights: number;
};

function isStayBlock(booking: PwaCalendarBooking): boolean {
  if (booking.externalSource === 'ical_min_stay') return true;
  return airbnbStayKind(booking) === 'block';
}

export function summarizeStayWindow(
  bookings: PwaCalendarBooking[],
  cabinCount: number,
  range: StayWindow,
): StayWindowStats {
  const confirmed: PwaCalendarBooking[] = [];
  const seen = new Set<string>();
  for (const booking of bookings) {
    if (booking.status !== 'confirmed' || seen.has(booking.id)) continue;
    seen.add(booking.id);
    confirmed.push(booking);
  }

  const days = eachLocalDay(range);
  const occupiedIds = new Set<string>();
  let occupiedNights = 0;
  for (const day of days) {
    const dayIds = new Set(getBookingsForDay(confirmed, day).map((booking) => booking.resourceId));
    occupiedNights += dayIds.size;
    for (const id of dayIds) occupiedIds.add(id);
  }

  const startKey = dateKeyFromDate(range.start);
  const endKey = dateKeyFromDate(range.end);
  let arrivals = 0;
  let departures = 0;
  for (const booking of confirmed) {
    if (isStayBlock(booking)) continue;
    const checkIn = bookingBoundaryDateKey(booking, 'start');
    const checkOut = bookingBoundaryDateKey(booking, 'end');
    if (checkIn && checkIn >= startKey && checkIn < endKey) arrivals += 1;
    if (checkOut && checkOut >= startKey && checkOut < endKey) departures += 1;
  }

  const occupiedCabins = occupiedIds.size;
  return {
    days: days.length,
    occupiedCabins,
    freeCabins: Math.max(0, cabinCount - occupiedCabins),
    cabinCount,
    arrivals,
    departures,
    occupiedNights,
  };
}
